import { useCallback, useEffect, useState } from 'react'
import { getMyWorkspaces, type WorkspaceSummary } from '@/features/onboarding/services/workspaceApi'
import { getCompanyProfile, upsertIdentity } from '../services/companyProfileApi'
import { notifyProfileCompletionChanged } from '../companyProfileCompletionBus'
// Deep import (not the icps index): the icps feature's components import from this one.
import { listIcps } from '@/features/icps/services/icpsApi'
import type { CompanyProfile, IdentityProfile } from '../types'

// When `workspaceId` is supplied (e.g. from SettingsPage, which already knows
// the current workspace) the hook skips its own workspace lookup and fetches
// the profile directly. Without it, the hook resolves the current workspace
// itself — used by standalone consumers like the dashboard-wide banner.
export function useCompanyProfile(workspaceId?: string) {
  const [workspace, setWorkspace] = useState<WorkspaceSummary | null>(null)
  const [profile, setProfile] = useState<CompanyProfile | null>(null)
  // Whether the workspace has at least one complete ICP (industry, size and job title all
  // set). Defaults to true so a failed lookup never nags — the profile counts as complete
  // only with this AND the identity fields (mirrors the backend's isIdentityComplete).
  const [hasCompleteIcp, setHasCompleteIcp] = useState(true)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      let targetId = workspaceId
      if (!targetId) {
        const workspaces = await getMyWorkspaces()
        const current = workspaces[0] ?? null
        setWorkspace(current)
        targetId = current?.id
      }
      if (targetId) {
        const [result, icps] = await Promise.all([
          getCompanyProfile(targetId),
          listIcps(targetId).catch(() => null),
        ])
        setProfile(result)
        if (icps) {
          setHasCompleteIcp(icps.some((icp) => icp.industries.length > 0 && icp.companySizes.length > 0 && icp.jobTitles.length > 0))
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load company profile')
    } finally {
      setLoading(false)
    }
  }, [workspaceId])

  useEffect(() => {
    load()
  }, [load])

  const persist = useCallback(async (write: (targetId: string) => Promise<CompanyProfile>) => {
    const targetId = workspaceId ?? workspace?.id
    if (!targetId) throw new Error('No workspace available')
    setSaving(true)
    setError(null)
    try {
      const updated = await write(targetId)
      setProfile(updated)
      // Tell any other mounted useCompanyProfile instance (e.g. the always-visible
      // CompanyProfileBanner) to recheck — see companyProfileCompletionBus.ts.
      notifyProfileCompletionChanged()
      return updated
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save company profile')
      throw err
    } finally {
      setSaving(false)
    }
  }, [workspaceId, workspace])

  const save = useCallback(
    (input: IdentityProfile) => persist((targetId) => upsertIdentity(targetId, input)),
    [persist],
  )

  return {
    workspace,
    profile,
    identityComplete: Boolean(profile?.identityCompletedAt),
    hasCompleteIcp,
    isComplete: Boolean(profile?.identityCompletedAt) && hasCompleteIcp,
    loading,
    saving,
    error,
    setError,
    reload: load,
    save,
  }
}
